import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_LANGUAGE, LanguageContext, SupportedLanguage, SUPPORTED_LANGUAGES, setCurrentLanguage as setGlobalLanguage } from '../services/languageService';
import enTranslations from './translations/en';
import esTranslations from './translations/es';
import frTranslations from './translations/fr';
import ptTranslations from './translations/pt';
import arTranslations from './translations/ar';
import deTranslations from './translations/de';
import jaTranslations from './translations/ja';
import koTranslations from './translations/ko';
import zhCNTranslations from './translations/zh-CN';
import zhTWTranslations from './translations/zh-TW';
import ruTranslations from './translations/ru';
import idTranslations from './translations/id';
import trTranslations from './translations/tr';
import viTranslations from './translations/vi';
import hiTranslations from './translations/hi';
import { pokedexTranslations } from './translations/pokedex';

/**
 * 基础翻译映射
 */
const translations: Record<SupportedLanguage, Record<string, string>> = {
  // Put `pokedexTranslations` first so per-language files can override defaults.
  en: { ...pokedexTranslations.en, ...enTranslations },
  es: { ...pokedexTranslations.es, ...esTranslations },
  fr: { ...pokedexTranslations.fr, ...frTranslations },
  pt: { ...pokedexTranslations.pt, ...ptTranslations },
  ar: { ...pokedexTranslations.ar, ...arTranslations },
  de: { ...pokedexTranslations.de, ...deTranslations },
  ja: { ...pokedexTranslations.ja, ...jaTranslations },
  ko: { ...pokedexTranslations.ko, ...koTranslations },
  'zh-CN': { ...pokedexTranslations['zh-CN'], ...zhCNTranslations },
  'zh-TW': { ...pokedexTranslations['zh-TW'], ...zhTWTranslations },
  ru: { ...pokedexTranslations.ru, ...ruTranslations },
  id: { ...pokedexTranslations.id, ...idTranslations },
  tr: { ...pokedexTranslations.tr, ...trTranslations },
  vi: { ...pokedexTranslations.vi, ...viTranslations },
  hi: { ...pokedexTranslations.hi, ...hiTranslations }
};

interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * 语言提供组件
 * 管理应用的语言状态和翻译功能
 */
export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const location = useLocation();
  
  /**
   * 从URL路径中获取语言代码
   * @param {string} path - 当前URL路径
   * @returns {SupportedLanguage} 检测到的语言代码或默认语言
   */
  const getLanguageFromPath = (path: string): SupportedLanguage => {
    const pathParts = path.split('/').filter(part => part !== '');
    if (pathParts.length === 0) {
      return getPreferredLanguage();
    }
    
    const pathLang = pathParts[0];
    if (SUPPORTED_LANGUAGES.some(lang => lang.code === pathLang)) {
      return pathLang as SupportedLanguage;
    }
    
    return getPreferredLanguage();
  };

  /**
   * 获取用户首选语言
   * 从本地存储或浏览器设置中获取
   */
  const getPreferredLanguage = (): SupportedLanguage => {
    // 首先检查localStorage中是否有保存的语言偏好
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang && SUPPORTED_LANGUAGES.some(lang => lang.code === savedLang)) {
      return savedLang as SupportedLanguage;
    }
    
    // 然后检查浏览器语言设置
    const browserLang = navigator.language;
    const exactMatch = SUPPORTED_LANGUAGES.find(lang => lang.code === browserLang);
    if (exactMatch) {
      return exactMatch.code;
    }
    
    // 检查语言的主要部分是否匹配（例如 'zh-CN' 匹配 'zh'）
    const mainLang = browserLang.split('-')[0];
    const partialMatch = SUPPORTED_LANGUAGES.find(lang => 
      lang.code === mainLang || lang.code.startsWith(`${mainLang}-`)
    );
    if (partialMatch) {
      return partialMatch.code;
    }
    
    // 默认返回英语
    return DEFAULT_LANGUAGE;
  };

  // 初始化当前语言状态
  const [currentLanguage, setCurrentLanguageState] = useState<SupportedLanguage>(
    getLanguageFromPath(location.pathname)
  );

  // 当路径变化时更新语言
  // 注意：只在语言真正改变时才更新，避免不必要的重新渲染
  useEffect(() => {
    const newLang = getLanguageFromPath(location.pathname);
    if (newLang !== currentLanguage) {
      // 从URL获取的语言优先级更高，直接使用
      setCurrentLanguageState(newLang);
      setGlobalLanguage(newLang);
    }
  }, [location.pathname]);

  // 初始化时设置全局语言
  useEffect(() => {
    // 确保全局语言与组件状态同步
    setGlobalLanguage(currentLanguage);
  }, []);

  // 为RTL语言应用正确的文本方向
  useEffect(() => {
    // 获取当前语言的方向
    const currentLangConfig = SUPPORTED_LANGUAGES.find(lang => lang.code === currentLanguage);
    const direction = currentLangConfig?.direction || 'ltr';
    
    // 应用方向和语言属性到HTML元素
    document.documentElement.dir = direction;
    document.documentElement.lang = currentLanguage;
    
    // 为RTL语言添加特殊类
    if (direction === 'rtl') {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  }, [currentLanguage]);

  /**
   * 翻译函数
   * 支持参数替换
   */
  const t = (key: string, params?: Record<string, any>): string => {
    // 获取当前语言的翻译，如果没有则使用默认语言
    const currentTranslations = translations[currentLanguage] || translations[DEFAULT_LANGUAGE];
    
    // 如果键不存在于当前语言，尝试在默认语言中查找
    let translatedText = currentTranslations[key];
    if (!translatedText && currentLanguage !== DEFAULT_LANGUAGE) {
      translatedText = translations[DEFAULT_LANGUAGE][key];
    }
    
    // 如果仍然找不到翻译，返回键本身
    if (!translatedText) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    
    // 如果有参数则替换
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        if (typeof translatedText === 'string') {
          translatedText = translatedText.replace(new RegExp(`{{${paramKey}}}`, 'g'), String(value));
        }
      });
    }
    
    return translatedText;
  };

  /**
   * 设置新的语言
   * @param {SupportedLanguage} lang - 要设置的语言代码
   */
  const setLanguage = (lang: SupportedLanguage) => {
    localStorage.setItem("preferredLanguage", lang);
    setCurrentLanguageState(lang);
    // 同时更新全局语言状态
    setGlobalLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
